import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useListAttributeKeys, useListAttributeValues } from "@workspace/api-client-react";
import { AttributeKeyCombobox } from "@/components/inventory/attribute-key-combobox";
import { AttributeValueCombobox } from "@/components/inventory/attribute-value-combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface SpecPair {
  label: string;
  value: string;
}

interface Props {
  value: SpecPair[] | null | undefined;
  onChange: (next: SpecPair[]) => void;
  // 2026-05-21: variant mode — spec keys are inherited from the parent and locked.
  // Operator can edit VALUES only; can't change keys, add, or remove rows.
  lockedKeys?: boolean;
}

const VOLUME_UNITS = ["ml", "l"] as const;
const WEIGHT_UNITS = ["gram", "kilogram"] as const;

// 2026-05-19 hot-fix: Specifications row mirrors Variant row's compound-input
// behavior for measurement-type attribute keys (Volume / Weight / Piece) — a
// numeric input plus a unit dropdown that compose into "500 ml" / "100 gram" /
// "12" stored on items.specs[].value. Other key types fall back to the
// AttributeValueCombobox dropdown (Brand, Colour, Flavour, etc.).
//
// One key per item: the AttributeKey combobox in each row excludes keys
// already chosen in other rows so the operator can't pick "Weight" twice.
export function ItemSpecsInput({ value, onChange, lockedKeys }: Props) {
  const specs = value ?? [];
  const usedLabels = specs.map((s) => s.label.trim().toLowerCase()).filter(Boolean);

  function addRow() {
    onChange([...specs, { label: "", value: "" }]);
  }

  function updateRow(idx: number, patch: Partial<SpecPair>) {
    onChange(specs.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  function removeRow(idx: number) {
    onChange(specs.filter((_, i) => i !== idx));
  }

  return (
    <div className="space-y-2">
      {specs.length === 0 ? (
        <div className="rounded-md border border-dashed px-3 py-3 text-xs text-muted-foreground">
          {lockedKeys
            ? "Parent has no specifications to inherit."
            : "No specifications yet. Click “+ Add Specification” below."}
        </div>
      ) : (
        <div className="space-y-2">
          {specs.map((s, i) => (
            <SpecRow
              key={i}
              spec={s}
              // Exclude keys already used in OTHER rows (one key per item).
              excludedLabels={usedLabels.filter((_, j) => j !== i)}
              onChange={(patch) => updateRow(i, patch)}
              onRemove={() => removeRow(i)}
              // Variant mode: the attribute KEY is inherited + locked, but the value
              // (incl. Weight/Volume number + unit split) is edited normally.
              lockedKey={lockedKeys}
            />
          ))}
        </div>
      )}
      {lockedKeys ? (
        <p className="text-[11px] text-muted-foreground">
          Spec keys are inherited from the parent item and locked. To add a new spec, edit the parent.
        </p>
      ) : (
      <Button type="button" size="sm" variant="outline" onClick={addRow}>
        <Plus className="mr-2 h-3 w-3" />
        Add Specification
      </Button>
      )}
    </div>
  );
}

// Parse a compound value string ("500 ml" / "100 gram" / "12") into qty + unit.
// Derived on every render — no separate state, so the unit dropdown can never
// desync from the persisted value (was the gram→kilogram save bug, 2026-05-20).
function parseCompound(raw: string): { qty: string; unit: string } {
  const m = /^([\d.]*)\s*([A-Za-z]*)$/.exec((raw ?? "").trim());
  return { qty: m?.[1] ?? "", unit: m?.[2] ?? "" };
}

function SpecRow({
  spec,
  excludedLabels,
  onChange,
  onRemove,
  lockedKey,
}: {
  spec: SpecPair;
  excludedLabels: string[];
  onChange: (patch: Partial<SpecPair>) => void;
  onRemove: () => void;
  lockedKey?: boolean;
}) {
  const [attributeKeyId, setAttributeKeyId] = useState<number | null>(null);
  const [attributeValueId, setAttributeValueId] = useState<number | null>(null);

  const { data: keys = [] } = useListAttributeKeys({ status: "Active" });
  const { data: values = [] } = useListAttributeValues(
    attributeKeyId != null ? { attributeKeyId, status: "Active" } : undefined,
    {
      query: {
        queryKey: ["listAttributeValues", attributeKeyId],
        enabled: attributeKeyId != null,
      },
    },
  );

  const selectedKey = keys.find((k) => k.id === attributeKeyId);
  const keyName = selectedKey?.name?.toLowerCase() ?? "";

  // Resolve persisted label string back to a key id when the keys list loads.
  useEffect(() => {
    if (attributeKeyId == null && spec.label && keys.length > 0) {
      const match = keys.find((k) => k.name.toLowerCase() === spec.label.toLowerCase());
      if (match) setAttributeKeyId(match.id);
    }
  }, [spec.label, keys, attributeKeyId]);

  // Resolve persisted value string back to a value id (for non-compound keys).
  const isCompound = keyName === "volume" || keyName === "weight" || keyName === "piece";
  useEffect(() => {
    if (
      !isCompound &&
      attributeKeyId != null &&
      attributeValueId == null &&
      spec.value &&
      values.length > 0
    ) {
      const match = values.find((v) => v.value.toLowerCase() === spec.value.toLowerCase());
      if (match) setAttributeValueId(match.id);
    }
  }, [spec.value, values, attributeKeyId, attributeValueId, isCompound]);

  function handleKeyChange(id: number) {
    const k = keys.find((x) => x.id === id);
    const nextLabel = k?.name ?? "";
    setAttributeKeyId(id);
    setAttributeValueId(null);
    onChange({ label: nextLabel, value: "" });
  }

  function handleValueChange(id: number, text?: string) {
    setAttributeValueId(id);
    // Prefer the text passed by the combobox (covers just-added values not yet in
    // the local list); fall back to the loaded list.
    const v = values.find((x) => x.id === id);
    onChange({ value: text ?? v?.value ?? "" });
  }

  // Compound value derived directly from spec.value (single source of truth).
  const { qty, unit } = parseCompound(spec.value);
  const defaultUnit = keyName === "volume" ? "ml" : "gram";
  const effectiveUnit = unit || defaultUnit;

  function setCompound(nextQty: string, nextUnit: string, mode: "measure" | "piece") {
    if (!nextQty) {
      onChange({ value: "" });
      return;
    }
    onChange({ value: mode === "piece" ? nextQty : `${nextQty} ${nextUnit}`.trim() });
  }

  return (
    <div className="rounded border p-3 bg-muted/20">
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Attribute Key</p>
          {lockedKey ? (
            <Input value={spec.label} readOnly disabled className="bg-muted/60 cursor-not-allowed" />
          ) : (
            <AttributeKeyCombobox
              value={attributeKeyId}
              onChange={handleKeyChange}
              excludeNames={excludedLabels}
            />
          )}
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Attribute Value</p>
          {keyName === "volume" || keyName === "weight" ? (
            <div className="flex gap-2">
              <Input
                type="number"
                min={0}
                placeholder="e.g. 500"
                className="w-28"
                value={qty}
                onChange={(e) => setCompound(e.target.value, effectiveUnit, "measure")}
              />
              <Select
                value={effectiveUnit}
                onValueChange={(v) => setCompound(qty, v, "measure")}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(keyName === "volume" ? VOLUME_UNITS : WEIGHT_UNITS).map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : keyName === "piece" ? (
            <Input
              type="number"
              min={1}
              placeholder="e.g. 6"
              value={qty}
              onChange={(e) => setCompound(e.target.value, "", "piece")}
            />
          ) : (
            <AttributeValueCombobox
              attributeKeyId={attributeKeyId}
              value={attributeValueId}
              valueText={spec.value}
              onChange={handleValueChange}
            />
          )}
        </div>
        {lockedKey ? (
          <span className="w-8" /> /* keep grid column; no remove for inherited specs */
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-destructive hover:text-destructive text-xs"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
